import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {finalize, forkJoin, map, Observable} from 'rxjs';
import * as _ from 'lodash';
import {VisitPlanModel} from "../../models/visit-plan.model";
import {EntityPaginationModel} from "../../../commons/models/entity-pagination.model";
import {VisitPlanAccountModel} from "../../models/visit-plan-account.model";
import {VisitPlanCellModel} from "../../models/visit-plan-cell.model";
import {VisitPlanSlotTrackerModel} from "../../models/visit-plan-slot-tracker.model";
import {VisitPlanCallModel} from "../../models/visit-plan-call.model";
import {RecurrencePatternModel} from "../../models/recurrence-pattern.model";
import {VisitScheduleResourceModel} from "../../models/visit-schedule-resource.model";
import {VisitScheduleModel} from "../../models/visit-schedule.model";
import {VisitPlanJobModel} from "../../models/visit-plan-job.model";
import {VisitScheduleDayModel} from "../../models/visit-schedule-day.model";
import {VisitPlanAccountAnalysisModel} from "../../models/visit-plan-account-analysis.model";
import {VisitPlanDayTotalsModel} from "../../models/visit-plan-day-totals.model";
import {VisitScheduleOptimizationSettings} from "../../models/visit-schedule-optimization-config.model";
import {CommonsFacadeService} from "../../../commons/angular/state/commons/commons-facade.service";
import { VisitScheduleResourceChanges } from "../../models/visit-schedule-resource-changes.model";
import { VisitScheduleRefreshParams } from "../../models/visit-schedule-refresh-params.model";
import {ProcessTrackingTypeEnum} from "../../../commons/types/process-tracking-type.enum";
import {ProcessTrackingStatusEnum} from "../../../commons/types/process-tracking-status.enum";
import {ProcessTrackingModel} from "../../../commons/models/process-tracking.model";
import {VisitScheduleAnalysisModel} from "../../models/visit-schedule-analysis.model";
import {
  VisitPlanTrackerCallRecurringConfigurationOptionsModel
} from "../../models/visit-plan-tracker-call-recurring-configuration-options.model";
import {VisitScheduleRouteOptimizeRequestModel} from "../../models/visit-schedule-route-optimize-request.model";
import {AccountModel} from "../../../account/angular/models/account.model";
import {
  AccountsImportBatchModel,
  AccountsImportValidateRowModel,
  AgentsImportBatchModel,
  AgentsImportStatusModel,
  AgentsImportValidateRowModel,
  DeliveryDaysValidateRowModel,
  ImportVisitPlanResponseModel
} from "../../models/visit-plan-imports.model";
import {VisitScheduleResourceHoldersParamsModel} from "../../models/visit-schedule-resource-holders-params.model";
import {ResourceSkill} from "../../../agents/types/resourceSkill.type";
import {VisitScheduleResourceHolderExt} from "../../models/visit-schedule-resource-holder-ext.model";
import {ResourceHoldersPaginationDataModel} from "../../models/resource-holders-pagination-data.model";
import {VisitScheduleAgentImportOptionModel} from "../../models/visit-schedule-agent-import-option.model";
import {ModelListWithCursor} from "../../../commons/angular/models/model-list-with-cursor.model";
import {AuditLogEntry} from "../../../commons/angular/services/audit-log-parser.service";
import {VisitPlanExternalAssignRequests} from "../../models/visit-plan-create-external-resource-holders-payload.model";
import {VisitScheduleOverviewModel} from "../../models/visit-schedule-overview.model";
import {VisitScheduleOverviewTotalsModel} from "../../models/visit-schedule-overview-totals.model";
import {VisitPlanExtendedServiceTimesModel} from "../../models/visit-plan-extended-service-times.model";
import {VisitPlanFilterModel} from "../../models/visit-plan-filter.model";

@Injectable({ providedIn: 'root' })
export class VisitPlanHttpService {

  private base_url: string = `/visitplan`;

  constructor(@Inject(HttpClient) private http: HttpClient, @Inject(CommonsFacadeService) private commonsFacadeService: CommonsFacadeService) {
  }

  queryVisitPlans(filter: VisitPlanFilterModel, params?: any): Observable<EntityPaginationModel<VisitPlanModel>> {
    return this.http
      .post<EntityPaginationModel<VisitPlanModel>>(`${this.base_url}/query`, filter, {params});
  }

  queryVisitPlansCursor(filter: VisitPlanFilterModel, params?: any): Observable<ModelListWithCursor<VisitPlanModel>> {
    return this.http
      .post<ModelListWithCursor<VisitPlanModel>>(`${this.base_url}/cursor/query`, filter, {params});
  }

  getVisitScheduleOverview(params?: any): Observable<EntityPaginationModel<VisitScheduleOverviewModel>> {
    return this.http
      .get<EntityPaginationModel<VisitScheduleOverviewModel>>(`${this.base_url}/schedule/overview`, {params});
  }

  getVisitScheduleOverviewTotals(params?: any): Observable<VisitScheduleOverviewTotalsModel> {
    return this.http
      .get<VisitScheduleOverviewTotalsModel>(`${this.base_url}/schedule/overview/totals`, {params});
  }

  getVisitPlan(visitPlanId: string): Observable<VisitPlanModel> {
    return this.http
      .get<VisitPlanModel>(this.base_url + `/${visitPlanId}`);
  }

  getVisitPlanProcessTracking(visitPlanId: string, params?: {type?: ProcessTrackingTypeEnum, status?: ProcessTrackingStatusEnum}): Observable<ProcessTrackingModel[]> {
    return this.http
      .get<any>(this.base_url + `/${visitPlanId}/processTracking`, {params});
  }

  createVisitPlan(visitPlan: VisitPlanModel): Observable<VisitPlanModel> {
    return this.http
      .put<VisitPlanModel>(this.base_url, visitPlan);
  }

  createTemplate(payload: any): Observable<any> {
    return this.http
      .put<any>(`${this.base_url}/template`, payload, { params: {} });
  }

  updateTemplate(templateId: string, payload: any): Observable<any> {
    return this.http
      .post<any>(`${this.base_url}/template/${templateId}`, payload, {  });
  }

  deleteTemplate(templateId: string): Observable<void> {
    return this.http
      .delete<void>(`${this.base_url}/template`, { params: {  ids: templateId } });
  }

  getTemplate(templateId: string): Observable<any> {
    return this.http
      .get<any>(`${this.base_url}/template/${templateId}`);
  }

  getTemplates(
    ids?: string,
    cursor?: string,
    limit?: number,
    params?: { branchIds?: string[]; global?: boolean }
  ): Observable<{ list: any[], cursor: string }> {
    const queryParams: any = {};
    if (ids) {
      queryParams.ids = ids;
    }
    if (cursor) {
      queryParams.cursor = cursor;
    }
    if (limit) {
      queryParams.limit = limit;
    }
    if (params?.branchIds?.length) {
      queryParams.branchIds = params.branchIds;
    }
    if (!_.isNil(params?.global)) {
      queryParams.global = params.global;
    }
    return this.http
      .get<{ list: any[], cursor: string }>(`${this.base_url}/template`, { params: queryParams });
  }

  getAllTemplates(
    limit?: number,
    branchIds?: string[]
  ): Observable<{ list: any[], cursor?: string }> {
    const scopedParams = branchIds?.length ? { branchIds, global: false } : { global: false };

    return forkJoin([
      this.getTemplates(undefined, undefined, limit, { global: true }),
      this.getTemplates(undefined, undefined, limit, scopedParams)
    ]).pipe(
      map(([globalTemplates, scopedTemplates]) => ({
        list: _.uniqBy([
          ...(globalTemplates.list || []),
          ...(scopedTemplates.list || [])
        ], 'id'),
        cursor: undefined
      }))
    );
  }

  updateVisitPlan(visitPlan: VisitPlanModel): Observable<VisitPlanModel> {
    return this.http
      .post<VisitPlanModel>(this.base_url + `/${visitPlan.id}`, visitPlan);
  }

  deleteVisitPlan(visitPlanId: string, force: boolean): Observable<void> {
    return this.http
      .delete<void>(this.base_url + `/${visitPlanId}`, { params: { force: force }});
  }

  restoreVisitPlan(visitPlanId: string): Observable<void> {
    return this.http
      .post<void>(this.base_url + `/${visitPlanId}/restore`, {});
  }

  getVisitPlanAccounts(visitPlanId: string, params?: any): Observable<{
    list: VisitPlanAccountModel[],
    cursor?: string
  }> {
    return this.http
      .get<{ list: VisitPlanAccountModel[], cursor?: string }>(this.base_url + `/${visitPlanId}/accounts`, {params});
  }

  getVisitPlanAccountsTotals(visitPlanId: string, params?: any): Observable<VisitPlanDayTotalsModel[]> {
    return this.http
      .get<VisitPlanDayTotalsModel[]>(this.base_url + `/${visitPlanId}/accounts/totals`, {params});
  }

  getVisitPlanAccountsMapData(visitPlanId: string): Observable<AccountModel[]> {
    return this.http
      .get<AccountModel[]>(this.base_url + `/${visitPlanId}/accounts/map`);
  }

  getVisitPlanAccountAnalysis(visitPlanId: string, params?: any): Observable<{
    list: VisitPlanAccountAnalysisModel[],
    cursor?: string
  }> {
    return this.http
      .get<{
        list: VisitPlanAccountAnalysisModel[],
        cursor?: string
      }>(this.base_url + `/${visitPlanId}/accounts/analysis`, {params});
  }

  deleteCellCalls(visitPlanId: string, visitCellId: string, visitCallIds: string[], deleteRecurringConfiguration?: boolean): Observable<VisitPlanCellModel> {
    return this.http
      .delete<VisitPlanCellModel>(this.base_url + `/${visitPlanId}/slot/${visitCellId}/call`, {
        params: {
          visitCallIds: visitCallIds,
          deleteRecurringConfiguration: deleteRecurringConfiguration
        }
      });
  }

  updateCellTracker(visitPlanId: string, visitPlanCell: VisitPlanCellModel, callIdConfigurationUpdateOptions?: Record<string, VisitPlanTrackerCallRecurringConfigurationOptionsModel>): Observable<VisitPlanSlotTrackerModel> {
    return this.http
      .post<VisitPlanSlotTrackerModel>(this.base_url + `/${visitPlanId}/slot/${visitPlanCell.id}/tracker`, {visitSlot: visitPlanCell, callIdConfigurationUpdateOptions: callIdConfigurationUpdateOptions});
  }

  getVisitSlots(visitPlanId: string, params: { callIds: string[], expand?: boolean, size?: number }): Observable<EntityPaginationModel<VisitPlanCellModel>> {
    return this.http
      .get<EntityPaginationModel<VisitPlanCellModel>>(this.base_url + `/${visitPlanId}/slot`, {params});
  }

  getExtendedServiceTimes(visitPlanId: string, accountId: string): Observable<VisitPlanExtendedServiceTimesModel> {
    return this.http
      .get<VisitPlanExtendedServiceTimesModel>(this.base_url + `/${visitPlanId}/slot/extended-service-times`, {
        params: { accountId }
      });
  }

  getServiceTimePrediction(visitPlanId: string, params: any): Observable<{ visitCallIdToPredictionMap: { [key: string]: number }}> {
    this.commonsFacadeService.setLoaderOn();
    return this.http
      .get<{ visitCallIdToPredictionMap: { [key: string]: number } }>(
        this.base_url + `/${visitPlanId}/slot/prediction`, { params })
      .pipe(
        finalize(() => this.commonsFacadeService.setLoaderOff())
      );
  }

  updateCallsTimeWindow(visitPlanId: string, cellId: string, list: {
    callId: string,
    notBefore: number,
    notAfter: number
  }[]): Observable<VisitPlanCellModel[]> {
    return this.http
      .post<VisitPlanCellModel[]>(this.base_url + `/${visitPlanId}/slot/${cellId}/call/timeWindows`, list);
  }

  createCall(visitPlanId: string, cellId: string, call: VisitPlanCallModel, recurrencePattern?: RecurrencePatternModel): Observable<VisitPlanCellModel> {
    return this.http
      .put<VisitPlanCellModel>(this.base_url + `/${visitPlanId}/slot/${cellId}/call`, {visitCall: call, recurrencePattern: recurrencePattern});
  }

  submitVisitPlanTracker(visitPlanId: string, includeIds?: string[], excludeIds?: string[]): Observable<VisitPlanModel> {
    const params: { includeIds?: string[], excludeIds?: string[] } = {}

    if (!!includeIds?.length) {
      params.includeIds = includeIds;
    }

    if (!!excludeIds?.length) {
      params.excludeIds = excludeIds;
    }
    return this.http
      .post<VisitPlanModel>(this.base_url + `/${visitPlanId}/tracker/submit`, {}, {
        params
      });
  }

  getTrackerChanges(visitPlanId: string, params: { size: number, page: number }): Observable<any> {
    return this.http
      .get<any>(this.base_url + `/${visitPlanId}/tracker/changes`, {
        params: params
      });
  }

  discardCellTrackers(visitPlanId: string, includeIds?: string[], excludeIds?: string[]): Observable<void> {
    const params: { includeIds?: string[], excludeIds?: string[] } = {}

    if (!!includeIds?.length) {
      params.includeIds = includeIds;
    }

    if (!!excludeIds?.length) {
      params.excludeIds = excludeIds;
    }

    return this.http
      .delete<void>(this.base_url + `/${visitPlanId}/slot/tracker`, {
        params
      });
  }

  initVisitSchedule(visitPlanId: string, visitScheduleConfig: VisitScheduleOptimizationSettings): Observable<VisitPlanModel> {
    return this.http
      .post<VisitPlanModel>(this.base_url + `/${visitPlanId}/schedule/init`, visitScheduleConfig, {});
  }

  getVisitSchedule(visitPlanId: string, visitScheduleId: string) {
    return this.http
      .get<VisitScheduleModel>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}`);
  }

  updateVisitSchedule(visitPlanId: string, visitScheduleId: string, visitSchedule: VisitScheduleModel): Observable<VisitScheduleModel> {
    return this.http
      .post<VisitScheduleModel>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}`, visitSchedule);
  }

  autoDispatchVisitSchedule(visitPlanId: string, visitScheduleId: string, visitSchedule: VisitScheduleModel): Observable<VisitScheduleModel> {
    return this.http
      .post<VisitScheduleModel>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/autoDispatch`, visitSchedule);
  }

  getVisitScheduleProcessTracking(visitPlanId: string, visitScheduleId: string, params?: {type?: ProcessTrackingTypeEnum, status?: ProcessTrackingStatusEnum}): Observable<ProcessTrackingModel[]> {
    return this.http
      .get<any>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/processTracking`, {params});
  }

  visitScheduleAbortPublish(visitPlanId: string, visitScheduleId: string): Observable<any> {
    return this.http
      .post<any>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/abortPublishing`, {});
  }

  visitScheduleAbort(visitPlanId: string, visitScheduleId: string): Observable<any> {
    return this.http
      .post<any>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/optimize/abort`, {});
  }

  getResourceHolders(visitPlanId: string, visitScheduleId: string, params: VisitScheduleResourceHoldersParamsModel): Observable<ResourceHoldersPaginationDataModel> {
    return this.http
      .get<ResourceHoldersPaginationDataModel>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/resources/holders/ext/page`, {
        params: params as any
      });
  }

  getResourceHoldersWithCursor(visitPlanId: string, visitScheduleId: string, params: any): Observable<{
    list: VisitScheduleResourceHolderExt[],
    cursor?: string
  }> {
    return this.http
      .get<{
        list: VisitScheduleResourceHolderExt[],
        cursor?: string
      }>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/resources/holders/ext`, {
        params: params as any
      });
  }

  getExternalAgentsResourceHoldersCursor(visitPlanId: string, visitScheduleId: string, params: any): Observable<{
    list: VisitScheduleAgentImportOptionModel[],
    cursor?: string
  }> {
    return this.http
      .get<{
        list: VisitScheduleAgentImportOptionModel[],
        cursor?: string
      }>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/resources/holders/external/agents`, {
        params: params as any
      });
  }

  importExternalAgentsResourceHolders(visitPlanId: string, visitScheduleId: string, agentIds: string[]): Observable<VisitScheduleResourceHolderExt[]> {
    return this.http
      .put<any>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/resources/holders/external`, agentIds, {});
  }

  importExternalAgentsResourceHoldersDesignated(visitPlanId: string, visitScheduleId: string, payload: VisitPlanExternalAssignRequests[]): Observable<VisitScheduleResourceHolderExt[]> {
    return this.http
      .put<any>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/resources/holders/external/designated`, payload, {});
  }

  getVisitScheduleDaysData(visitPlanId: string, visitScheduleId: string, params): Observable<{
    list: VisitScheduleDayModel[],
    cursor?: string
  }> {
    return this.http
      .get<{
        list: VisitScheduleDayModel[],
        cursor?: string
      }>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/days`, {
        params: params
      });
  }

  getResources(visitPlanId: string, visitScheduleId: string, params?: any): Observable<{
    list: VisitScheduleResourceModel[],
    cursor?: string
  }> {
    return this.http
      .get<{
        list: VisitScheduleResourceModel[],
        cursor?: string
      }>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/resources`, {params});
  }

  getResourcesList(visitPlanId: string, visitScheduleId: string, params?: any): Observable<VisitScheduleResourceModel[]> {
    return this.http
      .get<VisitScheduleResourceModel[]>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/resources/list`, {params});
  }

  getResourceConflicts(visitPlanId: string, visitScheduleId: string, params?: any): Observable<string[]> {
    return this.http
      .get<string[]>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/resources/conflicts`, {params});
  }

  getVisitPlanScheduleJobs(visitPlanId: string, visitScheduleId?: string, params?: any): Observable<{
    list: VisitPlanJobModel[],
    cursor?: string
  }> {
    return this.http
      .get<{
        list: VisitPlanJobModel[],
        cursor?: string
      }>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/jobs`, {params});
  }

  createVisitScheduleRoute(visitPlanId: string, visitScheduleId: string, payload: {
    resourceId: string,
    jobIds: string[]
  }): Observable<any> {
    return this.http
      .put<any>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/route`, payload, {});
  }

  getVisitScheduleRoutesPagination(visitPlanId: string, visitScheduleId: string, params?: any): Observable<any> {
    return this.http
      .get<any>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/route/page`, {params});
  }

  getVisitScheduleRoutesCursor(visitPlanId: string, visitScheduleId: string, params?: any): Observable<any> {
    return this.http
      .get<any>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/route`, {params});
  }

  getVisitScheduleAgentsUtilization(visitPlanId: string, visitScheduleId: string, params?: any): Observable<any> {
    return this.http
      .get<any>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/resources/utilization`, {params});
  }

  getVisitScheduleRisks(visitPlanId: string, visitScheduleId: string, params?: {agentIds?: string[], holderIds?: string[]}): Observable<VisitScheduleAnalysisModel> {
    return this.http
      .get<any>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/analysis`, {params});
  }

  getAssignOptions(visitPlanId: string, visitScheduleId: string, jobIds?: string[], callIds?: string[], requestedRouteId?: string, params?: any): Observable<any> {
    return this.http
      .post<any>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/resources/assign/options`, {jobIds: jobIds, callIds: callIds, requestedRouteId: requestedRouteId}, {params});
  }

  publishVisitSchedule(visitPlanId: string, visitScheduleId: string, params?: { routeIds?: string[], visitScheduleDayIds?: string[] }): Observable<any> {
    return this.http
      .post<any>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/publish`, {}, {params});
  }

  visitScheduleRouteOptimize(visitPlanId: string, visitScheduleId: string, visitScheduleRouteOptimizePayload: VisitScheduleRouteOptimizeRequestModel): Observable<any> {
    return this.http
      .post<any>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/route/optimizeIndividually`, visitScheduleRouteOptimizePayload, {});
  }

  deleteRoutes(visitPlanId: string, visitScheduleId: string, routeIds): Observable<any> {
    return this.http
      .delete<VisitPlanCellModel>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/route`, {
        params: {
          routeIds: routeIds
        }
      });
  }

  optimizeVisitSchedule(visitPlanId: string, visitScheduleId: string, visitScheduleOptimizationSettings: VisitScheduleOptimizationSettings, dayIds?: string[]): Observable<any> {
    return this.http
      .post<any>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/optimize`, {
        dayIds: dayIds || [], visitScheduleOptimizationSettings
      }, {});
  }

  dispatchVisitSchedule(visitPlanId: string, visitScheduleId: string, payload?: any): Observable<any> {
    return this.http
      .post<any>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/dispatch`, payload, {});
  }

  syncVisitSchedule(visitPlanId: string, visitScheduleId: string): Observable<VisitScheduleDayModel[]> {
    return this.http
      .post<VisitScheduleDayModel[]>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/sync`, {}, {});
  }

  syncVisitPlanDeliveryDetails(visitPlanId: string): Observable<ProcessTrackingModel> {
    return this.http
      .post<ProcessTrackingModel>(this.base_url + `/${visitPlanId}/slot/deliveryDetails`, {}, {});
  }

  editRoute(visitPlanId: string, visitScheduleId: string, payload, params = {}) {
    return this.http
      .post<VisitScheduleDayModel[]>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/route/edit`, payload, {
        params
      });
  }

  editResources(visitPlanId: string, visitScheduleId: string, resources: Array<{
    id: string;
    disabled: boolean;
    agentId: string;
    startTime: number | null;
    endTime: number | null;
    skills?: ResourceSkill[];
    branchIds?: string[];
    locationSettings?: unknown;
  }>): Observable<VisitScheduleResourceModel[]> {
    return this.http
      .post<VisitScheduleResourceModel[]>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/resources/availability/update`, resources);
  }

  createAvailability(visitPlanId: string, visitScheduleId: string, resources: Array<{
    resourceHolderId: string;
    startTime: number;
    endTime: number;
    branchIds?: string[];
    locationSettings?: unknown;
    skills?: ResourceSkill[];
  }>): Observable<VisitScheduleResourceModel[]> {
    return this.http
      .put<VisitScheduleResourceModel[]>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/resources/availability/create`, resources);
  }

  submitResources(visitPlanId: string, visitScheduleId: string): Observable<VisitScheduleResourceModel[]> {
    return this.http
      .post<VisitScheduleResourceModel[]>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/resources/submit`, {});
  }

  exportVisitPlanCalls(visitPlanId: string, visitScheduleId: string, siteId: string): Observable<any> {
    return this.http
      .post(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/export/calls/`, {}, {
        params: {
          siteId: siteId
        }
      });
  }

  exportAgentsSummary(visitPlanId: string, visitScheduleId: string, siteId: string): Observable<any> {
    return this.http
      .post(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/export/agents/`, {}, {
        params: {
          siteId: siteId
        }
      });
  }

  // removeDeliveries(visitPlanId: string, params: {accountId?: string, from?: number, to?: number}): Observable<any> {
  //   return this.http
  //     .delete<void>(this.base_url + `/${visitPlanId}/deliveries`, { params: params });
  // }

  removeDeliveries(visitPlanId: string, params: {
    accountIds?: string[],
    from?: number,
    to?: number
    projectBranchIds?: string[]
    areaIds?: string[]
    segments?: number[]
  }): Observable<any> {
    return this.http
      .delete<void>(this.base_url + `/${visitPlanId}/slot/deliveries`, { params: params });
  }

  removeCalls(visitPlanId: string, params: {
    accountIds?: string[],
    from?: number,
    to?: number
    projectBranchIds?: string[]
    areaIds?: string[]
    segments?: number[]
  }): Observable<any> {
    return this.http
      .delete<void>(this.base_url + `/${visitPlanId}/slot/bulkCalls`, { params: params });
  }

  exportVisitSchedule(visitPlanId: string, visitScheduleId: string, siteId: string): Observable<any> {
    return this.http
      .post(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/export/routes/`, {}, {
        params: {
          siteId: siteId
        }
      });
  }

  syncVisitPlanAccounts(visitPlanId: string, siteId: string): Observable<string> {
    return this.http
      .post(`${this.base_url}/${visitPlanId}/syncAccounts`, {}, {
        params: { siteId },
        responseType: 'text'
      });
  }

  syncVisitPlan(visitPlanId: string, accountIds: string[]): Observable<VisitPlanModel> {
    return this.http
      .post<VisitPlanModel>(this.base_url + `/${visitPlanId}/sync`, {accountIds}, {});
  }

  manualRefreshSchedule(visitPlanId: string, visitScheduleId: string, params: VisitScheduleRefreshParams): Observable<VisitScheduleResourceChanges[]> {
    const queryParams = {...params};
    return this.http
      .post<VisitScheduleResourceChanges[]>(
        this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/resources/schedule/upsert`,
        {},
        { params: queryParams }
      );
  }

  // Lock API methods
  lockRoutes(visitPlanId: string, visitScheduleId: string, siteId: string, lockState: boolean, ids: string[]): Observable<LockResponseModel> {
    return this.http
      .post<LockResponseModel>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/lock/routes`, {}, {
        params: { siteId, lockState, ids }
      });
  }

  lockResourceHolders(visitPlanId: string, visitScheduleId: string, siteId: string, lockState: boolean, ids: string[]): Observable<LockResponseModel> {
    return this.http
      .post<LockResponseModel>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/lock/resources/holders`, {}, {
        params: { siteId, lockState, ids }
      });
  }

  lockAccounts(visitPlanId: string, visitScheduleId: string, siteId: string, lockState: boolean, ids: string[]): Observable<LockResponseModel> {
    return this.http
      .post<LockResponseModel>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/lock/accounts`, {}, {
        params: { siteId, lockState, ids }
      });
  }

  lockDays(visitPlanId: string, visitScheduleId: string, siteId: string, lockState: boolean, ids: string[]): Observable<LockResponseModel> {
    return this.http
      .post<LockResponseModel>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/lock/days`, {}, {
        params: { siteId, lockState, ids }
      });
  }

  lockJobs(visitPlanId: string, visitScheduleId: string, siteId: string, lockState: boolean, ids: string[]): Observable<LockResponseModel> {
    return this.http
      .post<LockResponseModel>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/lock/jobs`, {}, {
        params: { siteId, lockState, ids }
      });
  }

  lockCalls(visitPlanId: string, visitScheduleId: string, siteId: string, lockState: boolean, ids: string[]): Observable<LockResponseModel> {
    return this.http
      .post<LockResponseModel>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/lock/calls`, {}, {
        params: { siteId, lockState, ids }
      });
  }

  getSkills(): Observable<any[]> {
    return this.http
      .get<any>(`/settings/skills`);
  }

  getRouteAssignmentOptions(visitPlanId: string, visitScheduleId: string, routeId: string, visitScheduleDayId: string): Observable<any> {
    const params = {
      routeId,
      visitScheduleDayId
    };
    return this.http
      .get<any>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/route/free/agents`, {params},);
  }

  moveRoute(visitPlanId: string, visitScheduleId: string, routeId: string, resourceId: string, visitScheduleDayId: string): Observable<any> {
    const params = {
      routeId,
      resourceId,
      visitScheduleDayId
    };
    return this.http
      .post<any>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/route/move`, {}, {params});
  }

  getTotalCph(visitPlanId: string, visitScheduleId: string): Observable<any> {
    return this.http
      .get<any>(this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/cph`);
  }

  importDeliveryDaysValidate(file: any, visitPlanId: string) {
    const formData = new FormData();
    formData.append('file', file);
    const params = new HttpParams().set('visitPlanId', visitPlanId);

    return this.http
      .post<DeliveryDaysValidateRowModel[]>(
        this.base_url + '/import/delivery/validate',
        formData,
        { params: params }
      );
  }

  importDeliveryDaysFromFile(visitPlanId: string, rows: DeliveryDaysValidateRowModel[]) {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const params = new HttpParams().set('visitPlanId', visitPlanId);

    return this.http
      .post<ImportVisitPlanResponseModel>(this.base_url + '/import/delivery/import', rows, { headers: headers, params: params });
  }

  importDeliveryDaysFromSystem(visitPlanId: string) {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const params = new HttpParams().set('visitPlanId', visitPlanId);

    return this.http
      .post<ImportVisitPlanResponseModel>(this.base_url + '/import/delivery/import/conf', null, { headers: headers, params: params });
  }

  validateAccountsImport(file: File, visitPlanId: string): Observable<AccountsImportValidateRowModel[]> {
    const formData = new FormData();
    formData.append('file', file);
    const params = new HttpParams().set('visitPlanId', visitPlanId);

    return this.http.post<AccountsImportValidateRowModel[]>(
      this.base_url + '/import/accounts/validate',
      formData,
      {params}
    );
  }

  importAccounts(
    visitPlanId: string,
    rows: AccountsImportValidateRowModel[]
  ): Observable<AccountsImportBatchModel | null> {
    const params = new HttpParams().set('visitPlanId', visitPlanId);

    return this.http.post<AccountsImportBatchModel | null>(
      this.base_url + '/import/accounts/import',
      rows,
      {params}
    );
  }

  validateAgentsImport(
    file: File,
    visitPlanId: string,
    visitScheduleId: string
  ): Observable<AgentsImportValidateRowModel[]> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<AgentsImportValidateRowModel[]>(
      this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/agents/validate`,
      formData
    );
  }

  importAgents(
    visitPlanId: string,
    visitScheduleId: string,
    rows: AgentsImportValidateRowModel[]
  ): Observable<AgentsImportBatchModel> {
    return this.http.post<AgentsImportBatchModel>(
      this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/agents/import`,
      rows
    );
  }

  getAgentsImportStatus(
    visitPlanId: string,
    visitScheduleId: string,
    batchId: string
  ): Observable<AgentsImportStatusModel> {
    return this.http.get<AgentsImportStatusModel>(
      this.base_url + `/${visitPlanId}/schedule/${visitScheduleId}/agents/import/status/${batchId}`
    );
  }

  getVisitPlanAuditLogs(visitPlanId: string, params?: {
    operations?: string;
    correlationId?: string;
    status?: string;
    actorType?: string;
    actorId?: string;
    from?: number;
    to?: number;
    limit?: number;
    cursor?: string;
  }): Observable<ModelListWithCursor<AuditLogEntry>> {
    return this.http
      .get<ModelListWithCursor<AuditLogEntry>>(`${this.base_url}/${visitPlanId}/action-records`, { params });
  }
}

export interface LockResponseModel {
  siteId: string;
  visitPlanId: string;
  visitScheduleId: string;
  lockState: boolean;
  jobs: string[];
  routes: string[];
  slots: string[];
  visitAccounts: string[];
  visitScheduleDays: string[];
  resourceHolders: string[];
}
